import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Button, Form, Loader,
} from 'semantic-ui-react';
import { useEligibilityTheme, useStatusHistoryFetcher } from '../../../hooks/useEligibilityEngine';
import './StatusHistoryModal.css';

const parseUTCDate = dateString => {
  if (!dateString) return new Date(NaN);
  // Ensure the string is treated as UTC if no timezone info is present
  const hasTimezone = /Z|[+-]\d{2}:\d{2}$/.test(dateString);
  return new Date(hasTimezone ? dateString : `${dateString}Z`);
};

const formatRelativeTime = dateString => {
  const now = new Date();
  const date = parseUTCDate(dateString);
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffWeeks < 5) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
  return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
};

const formatLocalDateTime = dateString => {
  const date = parseUTCDate(dateString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CHANGE_TYPE_LABELS = {
  CREATE: 'Create',
  UPDATE: 'Update',
  ACTIVATED: 'Activated',
};

// Config- and rule-scoped history rows share the same list-item shape; the
// scope-specific fields are read from `raw` in ExpandedDetails.
const mapHistoryItem = item => ({
  id: item.id,
  user: item.changedBy,
  changeType: item.changeType,
  changeTypeLabel: CHANGE_TYPE_LABELS[item.changeType] || item.changeType,
  relativeTime: formatRelativeTime(item.changedAt),
  absoluteTime: formatLocalDateTime(item.changedAt),
  raw: item,
});

const VISIBLE_RULE_FIELDS = ['parameters', 'actionOnFail', 'actionOnNull'];

const pickVisibleFields = snapshot => {
  if (!snapshot) return null;
  const picked = {};
  VISIBLE_RULE_FIELDS.forEach(key => {
    if (key in snapshot) picked[key] = snapshot[key];
  });
  return picked;
};

const RuleChangeDetail = ({ change }) => (
  <div className='rule-change-item'>
    <div className='rule-change-header'>
      <span className='rule-change-type'>{change.type}</span>
      <span className='rule-change-key'>{change.ruleKey}</span>
      {change.changedFields && change.changedFields.length > 0 && (
        <span className='rule-change-fields'>
          Changed: {change.changedFields.join(', ')}
        </span>
      )}
    </div>
    {(change.before || change.after) && (
      <div className='rule-change-diff'>
        {change.before && (
          <div className='rule-change-before'>
            <div className='diff-label'>Before</div>
            <pre>
              {JSON.stringify(pickVisibleFields(change.before), null, 2)}
            </pre>
          </div>
        )}
        {change.after && (
          <div className='rule-change-after'>
            <div className='diff-label'>After</div>
            <pre>
              {JSON.stringify(pickVisibleFields(change.after), null, 2)}
            </pre>
          </div>
        )}
      </div>
    )}
  </div>
);

RuleChangeDetail.propTypes = {
  change: PropTypes.shape({
    type: PropTypes.string,
    ruleKey: PropTypes.string,
    changedFields: PropTypes.arrayOf(PropTypes.string),
    before: PropTypes.object,
    after: PropTypes.object,
  }).isRequired,
};

const ExpandedDetails = ({ item, isRuleHistory }) => {
  const { raw } = item;
  const isActivated = raw.changeType === 'ACTIVATED';

  // Rule-scoped history (externalHistoryByRuleKey) exposes a single before/after
  // pair at the top level; config-scoped history nests multiple rule changes
  // under changeSummary.ruleChanges.
  const ruleChanges = isRuleHistory
    ? [
      {
        type: raw.changeType,
        ruleKey: raw.ruleKey,
        changedFields: raw.changedFields,
        before: raw.beforeValue,
        after: raw.afterValue,
      },
    ]
    : raw.changeSummary?.ruleChanges || [];

  return (
    <div className='history-details'>
      <table className='details-table'>
        <tbody>
          <tr data-testid='status-history-detail-config-id'>
            <td className='details-label'>Config ID</td>
            <td>{raw.configId}</td>
          </tr>
          <tr data-testid='status-history-detail-reason'>
            <td className='details-label'>Reason</td>
            <td>{raw.changeReason || '—'}</td>
          </tr>
          <tr data-testid='status-history-detail-changed-at'>
            <td className='details-label'>Changed At</td>
            <td>{formatLocalDateTime(raw.changedAt)}</td>
          </tr>
        </tbody>
      </table>

      {!isActivated && ruleChanges.length > 0 && (
        <div
          className='rule-changes-section'
          data-testid='status-history-rule-changes-section'
        >
          <div className='rule-changes-title'>Rule Changes</div>
          {ruleChanges.map((change, idx) => (
            <RuleChangeDetail key={change.ruleKey || idx} change={change} />
          ))}
        </div>
      )}
    </div>
  );
};

ExpandedDetails.propTypes = {
  item: PropTypes.shape({
    raw: PropTypes.object.isRequired,
  }).isRequired,
  isRuleHistory: PropTypes.bool,
};

ExpandedDetails.defaultProps = {
  isRuleHistory: false,
};

const StatusHistoryModal = ({
  open,
  onClose,
  fieldLabel,
  productCode,
  ruleKey,
}) => {
  const { themeClass } = useEligibilityTheme();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const { fetchHistory: fetchHistoryData } = useStatusHistoryFetcher();
  const isRuleHistory = !!ruleKey;

  const fetchHistory = useCallback(
    async(from, to) => {
      setLoading(true);
      setError('');
      try {
        const rawItems = await fetchHistoryData({
          productCode,
          ruleKey,
          from,
          to,
        });

        const mapped = (rawItems || []).map(mapHistoryItem);
        // Order chronologically, most recent first.
        mapped.sort(
          (a, b) => new Date(b.raw.changedAt) - new Date(a.raw.changedAt)
        );
        setHistoryData(mapped);
      } catch (err) {
        setError(err.message || 'Failed to load history');
        setHistoryData([]);
      } finally {
        setLoading(false);
      }
    },
    [productCode, ruleKey, fetchHistoryData]
  );

  useEffect(() => {
    if (open) {
      setExpandedId(null);
      fetchHistory();
    }
  }, [open, fetchHistory]);

  const handleSearch = () => {
    fetchHistory(startDate || undefined, endDate || undefined);
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    fetchHistory();
  };

  const toggleDetails = id => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  return (
    <Modal
      open={open}
      closeIcon
      onClose={onClose}
      closeOnEscape={false}
      closeOnRootNodeClick={false}
      size='large'
      className={`status-history-modal theme ${themeClass}`}
      data-testid='status-history-modal'
    >
      <Modal.Header data-testid='status-history-modal-header'>Status History - {fieldLabel}</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>
                  Start Date *
                  <input
                    id='startDate'
                    type='date'
                    placeholder='dd.mm.yyyy'
                    value={startDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={e => setStartDate(e.target.value)}
                    data-testid='status-history-start-date'
                  />
                </label>
              </Form.Field>
              <Form.Field>
                <label>
                  End Date
                  <input
                    id='endDate'
                    type='date'
                    placeholder='dd.mm.yyyy'
                    value={endDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={e => setEndDate(e.target.value)}
                    data-testid='status-history-end-date'
                  />
                </label>
              </Form.Field>
              <Form.Field className='filter-buttons-field'>
                <div className='filter-buttons' aria-label='filter-buttons'>
                  <Button
                    primary
                    onClick={handleSearch}
                    data-testid='status-history-search-button'
                  >
                    Search
                  </Button>
                  <Button
                    onClick={handleClear}
                    data-testid='status-history-clear-button'
                  >
                    Clear
                  </Button>
                </div>
              </Form.Field>
            </Form.Group>
          </Form>

          {loading && (
            <Loader
              active
              inline='centered'
              content='Loading history...'
              data-testid='status-history-loading'
            />
          )}

          {error && (
            <div className='history-error' data-testid='history-error'>
              {error}
            </div>
          )}

          {!loading && !error && (
            <div className='history-list'>
              {historyData.length === 0 ? (
                <div
                  className='history-empty'
                  data-testid='status-history-empty'
                >
                  No history records found.
                </div>
              ) : (
                historyData.map(item => (
                  <div
                    key={item.id}
                    className='history-item'
                    onClick={() => toggleDetails(item.id)}
                    role='button'
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && toggleDetails(item.id)}
                    data-testid='status-history-entry'
                    data-change-type={item.changeType}
                  >
                    <span
                      className={`history-text-badge change-type-${item.changeType?.toLowerCase()}`}
                      data-testid='status-history-entry-badge'
                    >
                      {item.changeTypeLabel}
                    </span>
                    <span
                      className='history-text-username'
                      data-testid='status-history-entry-user'
                    >
                      {item.user}
                    </span>
                    <span
                      className='history-text-reason'
                      data-testid='status-history-entry-reason'
                    >
                      {item.raw.changeReason || item.changeTypeLabel}
                    </span>
                    <div
                      className='history-timestamp'
                      data-testid='status-history-entry-timestamp'
                    >
                      {item.relativeTime} · {item.absoluteTime}
                    </div>
                    {expandedId === item.id && (
                      <div
                        className='history-item-expanded'
                        data-testid='status-history-entry-expanded'
                      >
                        <ExpandedDetails
                          item={item}
                          isRuleHistory={isRuleHistory}
                        />
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </Modal.Description>
      </Modal.Content>
    </Modal>
  );
};

StatusHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fieldLabel: PropTypes.string.isRequired,
  productCode: PropTypes.string.isRequired,
  ruleKey: PropTypes.string,
};

StatusHistoryModal.defaultProps = {
  ruleKey: undefined,
};

export default StatusHistoryModal;
